// User news containers: the 20 built-in PLATFORM cards plus the user's own
// THEME cards, and their order. Backend is live at
// REACT_APP_ONELIKE_BACKEND_API (https://api.onelike.app/service).
//
// Deliberately NOT following two patterns found elsewhere in src/services:
//
// 1. socialmedia.service.js keeps 15 copies of every call, one per
//    REACT_APP_ONELIKE_BACKEND_API_<LANG> var. Those vars point at different
//    controllers (/dienst, /servicefr, ...) which do NOT expose these
//    endpoints. The language is a request FIELD here, not a base URL, so this
//    service only ever uses the unsuffixed base.
// 2. newspin.service.js resolves bare res.json() with no res.ok check. Every
//    error branch of this API keys off the HTTP status code plus
//    body.errors[0], so each call resolves to { ok, status, body } instead.
//    Never read body.message (it carries a random "[Error number: ffnpgtd]"
//    prefix) and never body.status (an enum NAME like "BAD_REQUEST", not a
//    number).
//
// All mutations are POST; this API has no PUT and no DELETE.

import { authHeaderWithJSON } from "../helpers";
import { UserContainerClass } from "../models/UserContainer";

const API = process.env.REACT_APP_ONELIKE_BACKEND_API;

// POST /container does a live Google News fetch server-side (a proxy-liveness
// loop of up to 5s connect + 10s read per candidate, then an RSS fetch of
// 10s + 15s) and has no server-side overall cap, so it can legitimately run
// far longer than any other call in the app. 60s is the client-side cut-off.
const CREATE_TIMEOUT_MS = 60000;

// Same replacer as NewsSaveButton.handlePin in components/Item.js: applied to
// a fully-initialised UserContainerClass instance, it sends an absent field as
// "" rather than letting JSON.stringify drop the key entirely.
const replacer = (key, value) => {
    if (typeof value === 'undefined')
        return ""
    else return value;
}

// Exactly the five documented identity fields - never the whole
// authentication.user object, which also carries jwt, token and picture (and
// src/helpers/logger.js console.logs the full store on every action). The DTOs
// do declare @JsonIgnoreProperties(ignoreUnknown = true), but that is a safety
// net, not a licence.
function toIdentity(user) {
    const u = user || {};
    const identity = new UserContainerClass();
    identity.uuid = u.uuid;
    identity.firstname = u.firstname;
    identity.name = u.name; // surname, same identity block as PinDTO
    identity.email = u.email;
    identity.displayname = u.displayname;

    const { uuid, firstname, name, email, displayname } = JSON.parse(JSON.stringify(identity, replacer));
    return { uuid, firstname, name, email, displayname };
}

// Reads the body as text and tries JSON.parse, so an empty or non-JSON error
// body never throws (same defensive shape as apikey.actions.js).
function toResult(res) {
    return res.text().then(text => {
        let body;
        try {
            body = text ? JSON.parse(text) : undefined;
        } catch (e) {
            body = undefined;
        }
        return { ok: res.ok, status: res.status, body: body };
    });
}

// A rejected fetch (network/DNS/CORS) is reported as status 0, not thrown, so
// callers only ever have one shape to handle.
function networkResult() {
    return { ok: false, status: 0, body: undefined };
}

// Terminal guard - the same shape and name as filternews.service.js's
// settled(), so the two files read alike. Do not "simplify" it back to a bare
// .then(toResult, () => networkResult()).
//
// That two-arm shape catches a rejected fetch (network/DNS/CORS) but NOT a
// throw inside toResult itself: res.text() rejects on its own when the
// connection drops mid-body, and the rejection then passes straight through to
// the caller. The three thunks in usercontainer.actions.js attach a fulfilled
// handler only, so such a rejection dispatched NOTHING - no SUCCESS and no
// FAILURE - and usercontainer.reducer.js clears `loading` only in those cases.
// `loading` therefore stayed true in the STORE (not in component state), so
// CardConfigPopup's `busy` stayed true past a close and a reopen, and both of
// its exits are gated on it: onClose={busy ? undefined : handleCancel} and the
// Cancel button's disabled={busy}. Only a reload or a LOGOUT cleared it.
// create() below carries its own equivalent guard, for the same reason plus
// clearing its abort timer.
//
// So every call wrapped here resolves and none rejects: a body that cannot be
// read is reported exactly like a failed fetch - { ok: false, status: 0, body:
// undefined } - which is the marker CardConfigPopup's handleFailure already
// handles ("Can't reach OneLike...", then resync()).
function settled(promise) {
    return promise
        .then(toResult, () => networkResult())
        .catch(() => networkResult());
}

function post(path, payload) {
    return settled(fetch(`${API}${path}`,
        {
            method: 'POST',
            headers: authHeaderWithJSON(),
            body: JSON.stringify(payload)
        }));
}

// Both params are optional; the endpoint answers 200 always and never 404
// (an unknown uuid simply comes back with seeded: false).
function getMyContainers(uuid, language) {
    const params = [];
    if (uuid) {
        params.push(`uuid=${encodeURIComponent(uuid)}`);
    }
    if (language) {
        params.push(`language=${encodeURIComponent(language)}`);
    }
    const query = params.length > 0 ? `?${params.join('&')}` : '';

    return settled(fetch(`${API}/mycontainers${query}`,
        {
            method: 'GET',
            headers: authHeaderWithJSON()
        }));
}

// -> { created: true, container } on a new container, or 200 with
// { created: false, container } when the same user+language already has that
// title: an idempotent success, NOT an error.
//
// On abort (CREATE_TIMEOUT_MS) or network failure this resolves with
// outcome: 'unknown' rather than a failure - the row may already have been
// committed server-side, so the only honest next step is to re-fetch
// /mycontainers.
function create(user, title, language) {
    const payload = { ...toIdentity(user), title: title, language: language };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CREATE_TIMEOUT_MS);

    // Both arms, not just the fulfilled one: toResult itself can reject (an
    // aborted or dropped connection while res.text() is still reading the
    // body), and clearing the timer only on success left that case holding the
    // 60s timer AND rejecting the caller's promise - the old second .then()
    // had a fulfilled handler only, so the rejection passed straight through.
    // Nothing calls create() with a .catch(), so the effect was a popup stuck
    // on creating: true from CREATE_REQUEST, forever. Promise.finally
    // would also do it but is used nowhere else in this codebase, and it
    // re-raises the rejection - a rejecting create() is not a shape any caller
    // handles, so a body that cannot be read is reported the same way a failed
    // fetch is: outcome 'unknown', meaning "re-fetch /mycontainers to find
    // out". The abort path is unaffected - an aborted fetch rejects, which is
    // the second arm of the first .then() and still clears the timer below
    // (clearTimeout on an already-fired timer is a no-op).
    const settle = (result) => {
        clearTimeout(timer);
        return result;
    };

    return fetch(`${API}/container`,
        {
            method: 'POST',
            headers: authHeaderWithJSON(),
            body: JSON.stringify(payload),
            signal: controller.signal
        })
        .then(toResult, () => ({ ...networkResult(), outcome: 'unknown' }))
        .then(settle, () => settle({ ...networkResult(), outcome: 'unknown' }));
}

// -> { deleted: true }. POST, not DELETE - this API has no DELETE verb.
function deleteContainer(user, id) {
    return post('/container/delete', { ...toIdentity(user), id: id });
}

// -> { ordered: true, containers: [ ...the full list in its new order... ] }.
// The echoed list is authoritative; the caller must replace its state from it.
// Send the COMPLETE ordered list: omitted rows are appended, never deleted.
//
// The projection below is load-bearing. `list` is the merged render list and
// each of its elements carries ~90-100 articles in news[], so a spread or a
// map of whole elements would POST megabytes of article JSON on every single
// reorder. Send kind + name and nothing else.
function order(user, language, list) {
    const containers = (list || []).map(({ kind, name }) => ({ kind, name }));

    return post('/containers/order', { ...toIdentity(user), language: language, containers: containers });
}

export const usercontainerService = {
    getMyContainers,
    create,
    deleteContainer,
    order
};
